import { EventSourcePolyfill } from "event-source-polyfill";
import { axiosInstance } from "services/api";
import { getMercureHubUrl } from "services/apiMercure";

/** Совпадает с бэкендом App\Api\v2\Voice\Service\YandexTtsService */
export const YANDEX_TTS_VOICES = [
  { value: "alena", label: "Алёна" },
  { value: "filipp", label: "Филипп" },
  { value: "ermil", label: "Ермил" },
  { value: "jane", label: "Джейн" },
  { value: "omazh", label: "Омаж" },
  { value: "zahar", label: "Захар" },
  { value: "dasha", label: "Даша" },
  { value: "julia", label: "Юлия" },
  { value: "lera", label: "Лера" },
  { value: "masha", label: "Маша" },
  { value: "marina", label: "Марина" },
  { value: "alexander", label: "Александр" },
  { value: "kirill", label: "Кирилл" },
];

export const YANDEX_TTS_FORMATS = [
  { value: "oggopus", label: "Ogg Opus (рекомендуется)" },
  { value: "mp3", label: "MP3" },
  { value: "lpcm", label: "LPCM 8 kHz" },
];

/** Диапазон speed Yandex SpeechKit TTS (как на бэкенде YandexTtsService). */
export const YANDEX_TTS_SPEED_MIN = 0.1;
export const YANDEX_TTS_SPEED_MAX = 3.0;

/**
 * Общая сессия Mercure для страницы «Заявки на сборку»: все вкладки подписаны на voice-stream-{id}.
 * Инициатор вещания передаёт тот же broadcastSessionId + relayId, чтобы не дублировать звук у себя.
 */
export const ORDER_PICKING_VOICE_BROADCAST_SESSION_ID = "orderPickingVoice";

/** Полное имя топика Mercure (должно совпадать с topicVoiceStream на бэкенде). */
export const ORDER_PICKING_VOICE_MERCURE_TOPIC = `voice-stream-${ORDER_PICKING_VOICE_BROADCAST_SESSION_ID}`;

export const VOICE_ORDER_PICKING_SKIP_RELAY_EVENT = "voiceOrderPickingSkipRelay";

export function dispatchSkipVoiceRelayPlayback(relayId) {
  window.dispatchEvent(
    new CustomEvent(VOICE_ORDER_PICKING_SKIP_RELAY_EVENT, { detail: { relayId } })
  );
}

export function mimeFromVoiceFormatForRelay(format) {
  if (format === "mp3") return "audio/mpeg";
  if (format === "lpcm") return "audio/pcm";
  return "audio/ogg";
}

function decodeAxiosErrorMessage(error) {
  const data = error.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder().decode(data);
      const json = JSON.parse(text);
      return json.message || json.detail || text;
    } catch {
      return "Ошибка синтеза";
    }
  }
  if (error.response?.data?.message) return error.response.data.message;
  return error.message || "Ошибка запроса";
}

/**
 * Синтез речи (бинарный ответ). Учитывает axios-интерсептор (401 / refresh).
 */
export async function synthesizeVoiceRequest({ text, voice, format, broadcastSessionId, speed, relayId }) {
  try {
    const body = { text, voice, format, ...(broadcastSessionId ? { broadcastSessionId } : {}) };
    if (relayId != null && relayId !== "") {
      body.relayId = String(relayId);
    }
    if (speed != null && speed !== "") {
      body.speed = Number(speed);
    }
    const res = await axiosInstance.post("/api/v2/voice/synthesize", body, { responseType: "arraybuffer" });
    return res.data;
  } catch (e) {
    throw new Error(decodeAxiosErrorMessage(e));
  }
}

export async function createVoiceShareLinkBody({ text, voice, format, speed }) {
  try {
    const body = { text, voice, format };
    if (speed != null && speed !== "") {
      body.speed = Number(speed);
    }
    const { data } = await axiosInstance.post("/api/v2/voice/shareLink", body);
    return data;
  } catch (e) {
    throw new Error(decodeAxiosErrorMessage(e));
  }
}

export async function fetchVoiceMercureSubscribePayload(sessionId) {
  try {
    const { data } = await axiosInstance.get("/api/v2/voice/mercureToken", {
      params: { sessionId },
    });
    return data;
  } catch (e) {
    throw new Error(decodeAxiosErrorMessage(e));
  }
}

function resolveMercureHubUrl(mercurePublicUrlFromApi) {
  const raw = mercurePublicUrlFromApi || getMercureHubUrl() || "";
  const trimmed = String(raw).replace(/\/$/, "");
  if (!trimmed) return "";
  return trimmed.includes(".well-known") ? trimmed : `${trimmed}/.well-known/mercure`;
}

/**
 * EventSource на топик voice-stream-{sessionId} (JWT только на этот топик).
 */
export async function createVoiceBroadcastEventSource(sessionId) {
  const { token, topic, mercureUrl } = await fetchVoiceMercureSubscribePayload(sessionId);
  const hub = resolveMercureHubUrl(mercureUrl);
  if (!hub || !token || !topic) return null;
  const url = new URL(hub);
  url.searchParams.set("topic", topic);
  return new EventSourcePolyfill(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
    heartbeatTimeout: 30000,
  });
}

export function playAudioArrayBuffer(arrayBuffer, mimeType = "audio/ogg") {
  const blob = new Blob([arrayBuffer], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const audio = new Audio(objectUrl);
  const done = () => URL.revokeObjectURL(objectUrl);
  audio.addEventListener("ended", done);
  audio.addEventListener("error", done);
  return audio.play().catch((err) => {
    done();
    throw err;
  });
}

/**
 * Параллельно: скачивание того же синтеза через HTTP и сбор копии из Mercure (b64 до last).
 */
export async function waitForVoiceMercurePlayback(sessionId, synthesizePromise) {
  const es = await createVoiceBroadcastEventSource(sessionId);
  if (!es) throw new Error("Mercure недоступен или не настроен");

  await new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("Таймаут подключения к Mercure")), 8000);
    es.onopen = () => {
      clearTimeout(t);
      resolve();
    };
    es.onerror = () => {
      clearTimeout(t);
      reject(new Error("Ошибка Mercure"));
    };
  });

  const chunkArrays = [];

  const audioPromise = new Promise((resolve, reject) => {
    es.onmessage = (event) => {
      try {
        const d = JSON.parse(event.data || "{}");
        if (d.start) {
          chunkArrays = [];
          return;
        }
        if (d.b64) {
          const bin = Uint8Array.from(atob(d.b64), (c) => c.charCodeAt(0));
          chunkArrays.push(bin);
        }
        if (d.last) {
          es.close();
          const total = chunkArrays.reduce((s, a) => s + a.length, 0);
          const merged = new Uint8Array(total);
          let off = 0;
          for (const a of chunkArrays) {
            merged.set(a, off);
            off += a.length;
          }
          resolve(merged.buffer);
        }
      } catch (err) {
        es.close();
        reject(err);
      }
    };
  });

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Таймаут ожидания чанков Mercure")), 120000)
  );

  try {
    const [, buffer] = await Promise.race([
      Promise.all([synthesizePromise, audioPromise]),
      timeout,
    ]);
    return buffer;
  } catch (e) {
    try {
      es.close();
    } catch {
      /* ignore */
    }
    throw e;
  }
}
